import { useRef, useState, Children, cloneElement, useEffect, useContext } from 'react'
import { useControls } from 'leva'
import { OrbitControls, PerspectiveCamera } from '@react-three/drei'
import { useThree, useFrame } from '@react-three/fiber'
import { Vector3 } from 'three'
import { AppConfigContext } from '../../../../../../context/AppConfig'
import { zoomInAnimation, zoomOutAnimation, resetViewAnimation } from './animations'
import { calculateCameraPositionAgainstModel, calculateCameraDistance, calculateCameraPositionTopViewAgainstModel } from './utils'
import { sendIFrameEvent } from '../../../../../../helpers/iframe'

let modelLastY = null

function controlPanAction({ controlsRef, camera, model }) {
  if (controlsRef.current && model) {
    const MIN_WORLD_Y = -1
    const MAX_WORLD_Y = 8
    let modelPosition = model.position.clone()
    modelPosition.project(camera)
    const controls = controlsRef.current

    if (modelLastY === null) {
      modelLastY = modelPosition.y;
    }
    const movingDown = modelPosition.y < modelLastY
    modelLastY = modelPosition.y

    if (!movingDown && controls.target.y >= MAX_WORLD_Y) {
      controls.target.y = MAX_WORLD_Y
    }

    if (controls.target.y <= MIN_WORLD_Y) {
      controls.target.y = MIN_WORLD_Y
    }

    // Ensure camera is not below the ground
    if (camera.position.y <= Math.PI / 2 - 0.1) {
      // camera.position.y = Math.PI / 2 - 0.1
    }

    // if (controlsRef.current) {
    //   console.log(model.position)
    //   // controlsRef.current.target.set(0, 0, 0); // New center of rotation
    //   controlsRef.current.update(); // Apply the changes
    // }

    controls.update()
  }
}

export function CameraExtension({ rotationAngle, scaleFactor, model, children, selectedLine, setCameraConfigCb, isAnimateFromTopView, isVerticalRotationDone }) {
  rotationAngle = !isNaN(Number(rotationAngle)) ? Number(rotationAngle) : 0
  const fov = 35
  const initialTargetPosition = new Vector3(0, 0, 0)
  // const rotateSpeed = 0.75
  const { offsetWidth: width, offsetHeight: height } = document.getElementById('root')
  let aspectRatio = +(width / height).toFixed(2)
  const controlsRef = useRef()
  const { camera } = useThree()
  const [isUserInteracting, setIsUserInteracting] = useState(false)
  const [animation, setAnimation] = useState(null)
  const [cameraConfig, setCameraConfig] = useState({})
  const [selectedLineState, setSelectedLine] = useState()
  const [modelDistanceMaximized, setModelDistanceMaximized] = useState()
  const [,setPositon] = useControls('Camera', () => ({ position: { value: camera.position, disabled: true } }))

  useEffect(() => {
    if (model) {
      if (selectedLine && selectedLine.name !== selectedLineState) {
        setSelectedLine(selectedLine.name)
        setModelDistanceMaximized(true)
      } else {
        setModelDistanceMaximized(false)
      }
    }
  }, [selectedLine, model])

  useEffect(() => {
    let cameraPositionCoordinates = null
    let cameraPosition = null
    let cameraDistances = null
    
    if (model) {
      if (rotationAngle) {
       
        if (isAnimateFromTopView) {
          cameraPositionCoordinates = calculateCameraPositionTopViewAgainstModel({ model, scaleFactor, fov, aspectRatio })
        } else {
          cameraPositionCoordinates = calculateCameraPositionAgainstModel({ model, rotationAngle, scaleFactor, fov, aspectRatio })
        }
        
        cameraPosition = new Vector3(...cameraPositionCoordinates)
        cameraDistances = calculateCameraDistance({ cameraPosition, model, rotationAngle, fov, aspectRatio, scaleFactor })
      }

      if (cameraConfig.cameraPosition) {
        if (modelDistanceMaximized || aspectRatio !== cameraConfig.aspectRatio || scaleFactor !== cameraConfig.scaleFactor) {
          const distance = camera.position.clone().distanceTo(new Vector3(0, 0, 0))
          const zoomFactor = cameraDistances.maxDistance / distance / scaleFactor
          cameraPosition = camera.position.clone().multiplyScalar(zoomFactor)
        } else {
          cameraPosition = camera.position.clone()
        }
      }

      const conf = {
        ...cameraDistances,
        maxPolarAngle: Math.PI / 2 - 0.1,
        aspectRatio,
        scaleFactor,
        cameraPosition,
        cameraPositionCoordinates,
      }
      setCameraConfig(conf)
      setCameraConfigCb(conf)
    }
  }, [model, modelDistanceMaximized, rotationAngle, aspectRatio, isAnimateFromTopView])

  // Real-time updates with useFrame for smooth interpolation
  useFrame(() => {
    resetViewAnimation({ camera, cameraConfig, animation, initialTargetPosition, controlsRef, setAnimation, setIsUserInteracting })
    zoomInAnimation({ camera, cameraConfig, animation, setAnimation })
    zoomOutAnimation({ camera, cameraConfig, animation, setAnimation })
    controlPanAction({ controlsRef, camera, model })

    const {x, y, z} = camera?.position
    if (x && y && z) {
      setPositon({ position: [x,y,z] })
    }
  })

  useEffect(() => {
    const listener = () => {
      setIsUserInteracting(false)
    }
    const resetViewAction = () => {
      if (isUserInteracting) {
        setAnimation('reseting')
      }
    }

    document.addEventListener('reset-view-event', resetViewAction)
    document.addEventListener('proceed-animation', listener)

    return () => {
      document.removeEventListener('reset-view-event', resetViewAction)
      document.removeEventListener('proceed-animation', listener)
    }
  })

  useEffect(() => {
    if (isUserInteracting) {
      sendIFrameEvent('analytics', {event: 'engaged_with_model'})
    }
  }, [isUserInteracting])

  const renderChildren = () => {
    return Children.map(children, (child) => {
      return cloneElement(child, {
        controlsRef,
      })
    })
  }

  useEffect(() => {
    const adjustZoom = ({ detail }) => {
      if (detail.direction === 'zoom-in') {
        setAnimation('zoomIn')
      }
      if (detail.direction === 'zoom-out') {
        setAnimation('zoomOut')
      }
    }

    document.addEventListener('adjust-zoom', adjustZoom)

    return () => {
      document.removeEventListener('adjust-zoom', adjustZoom)
    }
  }, [cameraConfig, camera])

  const offset = model?.rotation.x || 0;

  const minPolarAngle = 0.02 + offset;
  const maxPolarAngle = (Math.PI / 2 - 0.1) + offset;

  return (
    <>
      <OrbitControls
        ref={controlsRef}
        autoRotate={false}
        // autoRotateSpeed={rotateSpeed}
        enabled={isVerticalRotationDone}
        enableTouchZoom={!animation}
        enableTouchRotate={!animation}
        enableZoom={!animation}
        enableRotate={!animation}
        enablePan={true}
        minAzimuthAngle={0}
        maxAzimuthAngle={0}
        camera={camera}
        minPolarAngle={minPolarAngle}
        maxPolarAngle={maxPolarAngle}
        onStart={() => {
          setIsUserInteracting(true)
        }} // Disable animation when the user starts interacting
        onEnd={() => {
          setIsUserInteracting(true)
          setAnimation(null)
        }}
        maxDistance={cameraConfig?.maxDistance}
        minDistance={cameraConfig?.minDistance}
      />
      <PerspectiveCamera
        makeDefault
        fov={fov}
        position={cameraConfig?.cameraPosition}
        far={cameraConfig?.far}
        near={cameraConfig?.near || 1}
      />
      {renderChildren()}
    </>
  )
}
